document.documentElement.classList.remove('no-js');

class CartDrawer {
  constructor(drawer) {
    this.drawer = drawer;
    this.panel = drawer.querySelector('[data-cart-panel]');
    this.items = drawer.querySelector('[data-cart-items]');
    this.total = drawer.querySelector('[data-cart-total]');
    this.checkout = drawer.querySelector('[data-cart-checkout]');
    this.status = drawer.querySelector('[data-cart-status]');
    this.currency = drawer.dataset.currency || 'USD';
    this.root = window.Shopify?.routes?.root || '/';
    this.lastFocus = null;
    this.closeTimer = null;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.currency
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  escape(value = '') {
    const element = document.createElement('div');
    element.textContent = String(value);
    return element.innerHTML;
  }

  async request(path, options = {}) {
    const response = await fetch(`${this.root}${path}`, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) }
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Cart could not be updated.');
    return data;
  }

  async getCart() { return this.request('cart.js'); }

  render(cart) {
    document.querySelectorAll('.cart-count').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });

    if (!cart.items.length) {
      this.items.innerHTML = `
        <div class="drawer-empty">
          <span class="drawer-empty__mark" aria-hidden="true">◇</span>
          <h3>Your cart is empty</h3>
          <p>Your copy is still waiting in the archive.</p>
          <button class="button button--outline button--wide" type="button" data-cart-close>Continue reading</button>
        </div>`;
      this.total.textContent = this.formatMoney(0);
      this.checkout.hidden = true;
      return;
    }

    this.items.innerHTML = cart.items.map((item) => {
      const image = item.featured_image?.url || item.image || '';
      const separator = image.includes('?') ? '&' : '?';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="drawer-product__variant">${this.escape(item.variant_title)}</p>` : '';
      return `
        <article class="drawer-product" data-cart-line="${this.escape(item.key)}">
          <a class="drawer-product__image" href="${this.escape(item.url)}" tabindex="-1">
            ${image ? `<img src="${this.escape(image)}${separator}width=240" alt="" width="92" height="122" loading="lazy">` : ''}
          </a>
          <div class="drawer-product__content">
            <a class="drawer-product__title" href="${this.escape(item.url)}">${this.escape(item.product_title)}</a>
            ${variant}
            <p class="drawer-product__unit">${this.formatMoney(item.final_price)}</p>
            <div class="drawer-product__controls" aria-label="Quantity for ${this.escape(item.product_title)}">
              <button type="button" data-cart-quantity="${item.quantity - 1}" data-cart-key="${this.escape(item.key)}" aria-label="Decrease quantity">−</button>
              <span aria-live="polite">${item.quantity}</span>
              <button type="button" data-cart-quantity="${item.quantity + 1}" data-cart-key="${this.escape(item.key)}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <div class="drawer-product__aside">
            <strong>${this.formatMoney(item.final_line_price)}</strong>
            <button class="drawer-product__remove" type="button" data-cart-quantity="0" data-cart-key="${this.escape(item.key)}" aria-label="Remove ${this.escape(item.product_title)}">Remove</button>
          </div>
        </article>`;
    }).join('');
    this.total.textContent = this.formatMoney(cart.total_price);
    this.checkout.hidden = false;
  }

  async refresh() {
    const cart = await this.getCart();
    this.render(cart);
    return cart;
  }

  async add(form) {
    const button = form.querySelector('[type="submit"]');
    const original = button?.innerHTML;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
    }
    this.setStatus('Adding your ebook to the cart.');
    try {
      await this.request('cart/add.js', { method: 'POST', body: new FormData(form) });
      await this.refresh();
      this.open(button);
      this.setStatus('The ebook was added to your cart.');
    } catch (error) {
      this.setStatus(error.message, true);
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = original;
      }
    }
  }

  async change(key, quantity, button) {
    this.drawer.classList.add('is-loading');
    if (button) button.disabled = true;
    try {
      const cart = await this.request('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) })
      });
      this.render(cart);
      this.setStatus(Number(quantity) === 0 ? 'Item removed from the cart.' : 'Cart quantity updated.');
    } catch (error) {
      this.setStatus(error.message, true);
      await this.refresh();
    } finally {
      this.drawer.classList.remove('is-loading');
    }
  }

  setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.error = String(isError);
  }

  open(trigger) {
    clearTimeout(this.closeTimer);
    this.lastFocus = trigger || document.activeElement;
    this.drawer.hidden = false;
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('locked');
    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
      this.drawer.querySelector('[data-cart-close]')?.focus();
    });
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('locked');
    this.closeTimer = window.setTimeout(() => { this.drawer.hidden = true; }, 320);
    this.lastFocus?.focus?.();
  }

  trapFocus(event) {
    if (event.key !== 'Tab' || this.drawer.hidden) return;
    const focusable = [...this.panel.querySelectorAll('a[href],button:not([disabled]),select,input,[tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const drawerElement = document.querySelector('[data-cart-drawer]');
  const cartDrawer = drawerElement ? new CartDrawer(drawerElement) : null;

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('form[action*="/cart/add"]');
    if (!form || !cartDrawer) return;
    event.preventDefault(); cartDrawer.add(form);
  });

  document.addEventListener('click', async (event) => {
    const menuButton = event.target.closest('[data-menu-toggle]');
    if (menuButton) {
      const menu = document.querySelector('[data-mobile-menu]');
      const open = menuButton.getAttribute('aria-expanded') === 'true';
      menuButton.setAttribute('aria-expanded', String(!open));
      if (menu) menu.hidden = open;
      document.body.classList.toggle('locked', !open);
      return;
    }
    const cartButton = event.target.closest('[data-cart-toggle]');
    if (cartButton && cartDrawer) {
      try { await cartDrawer.refresh(); cartDrawer.open(cartButton); }
      catch (error) { cartDrawer.setStatus(error.message, true); cartDrawer.open(cartButton); }
      return;
    }
    const closeButton = event.target.closest('[data-cart-close]');
    if (closeButton && cartDrawer) { cartDrawer.close(); return; }
    if (event.target === drawerElement && cartDrawer) { cartDrawer.close(); return; }
    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (quantityButton && cartDrawer) {
      cartDrawer.change(quantityButton.dataset.cartKey, quantityButton.dataset.cartQuantity, quantityButton);
      return;
    }
    const menuLink = event.target.closest('[data-mobile-menu] a');
    if (menuLink) {
      const menu = document.querySelector('[data-mobile-menu]');
      const toggle = document.querySelector('[data-menu-toggle]');
      if (menu) menu.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('locked');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const menu = document.querySelector('[data-mobile-menu]');
      const toggle = document.querySelector('[data-menu-toggle]');
      if (menu) menu.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (cartDrawer && !drawerElement.hidden) cartDrawer.close();
      document.body.classList.remove('locked');
      return;
    }
    cartDrawer?.trapFocus(event);
  });

  document.addEventListener('toggle', (event) => {
    const item = event.target;
    if (!item.matches?.('[data-accordion]') || !item.open) return;
    item.closest('[data-accordion-group]')?.querySelectorAll('details[open]').forEach((other) => {
      if (other !== item) other.open = false;
    });
  }, true);

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-variant-select]');
    if (!select) return;
    const price = select.selectedOptions[0]?.dataset.price;
    const target = select.closest('.product-offer,.product-main')?.querySelector('[data-live-price]');
    if (price && target) target.textContent = price;
  });

  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.target.classList.toggle('visible', entry.isIntersecting));
    }, { threshold: 0.12 });
    document.querySelectorAll('[data-reveal]').forEach((element) => observer.observe(element));
  } else {
    document.querySelectorAll('[data-reveal]').forEach((element) => element.classList.add('visible'));
  }
});
